/**
 * Bringing a renderer up, with the WebGL fallback that `renderer-mode.ts`
 * decided was allowed — and the mid-session device loss that sends the page
 * back through the same decision.
 *
 * ## Why one `catch` covers three failure modes
 *
 * The brief named three ways a WebGPU boot dies: `requestAdapter` producing
 * nothing, `requestDevice` rejecting, and `renderer.init()` throwing for some
 * other reason. They are three *messages*, not three code paths. three's
 * `WebGPUBackend.init()` performs the adapter request and the device request
 * itself (`renderers/webgpu/WebGPUBackend.js`), and `Renderer.init()` wraps the
 * whole of it in one `try`. Left alone it would then quietly retry on a WebGL2
 * backend — which is the thing `disableWebGlFallback` exists to forbid — and
 * with `_getFallback` nulled it **rejects** instead, with the underlying error.
 *
 * So the state machine probes once, catches once, and *classifies* afterwards.
 * The alternative considered and rejected was a pre-probe: call
 * `navigator.gpu.requestAdapter()` and `adapter.requestDevice()` ourselves to
 * name the failure precisely, then hand three the device it accepts as
 * `parameters.device`. It would have cost a second device creation on the happy
 * path for a distinction that changes nothing about what happens next — every
 * one of the three falls back to exactly the same WebGL renderer with exactly
 * the same persisted record. The reason is a *report*, and a report may be
 * derived after the fact.
 *
 * **`classifyRendererInitFailure` is therefore string matching, and it is
 * confined to the reason string.** three throws plain `Error`s with no code and
 * no subclass, and a browser's `requestDevice` rejection is a `DOMException`
 * whose message is entirely up to the implementation. Nothing branches on the
 * result: the fallback happens whatever it says, so a misclassification costs a
 * wrong word in a diagnostic and never a wrong backend.
 *
 * ## Why the renderer is rebuilt rather than re-inited
 *
 * `Renderer.init()` memoises its promise in `_initPromise` and returns the same
 * rejected one forever after (three, `renderers/common/Renderer.js`), so the
 * failed object cannot be retried and its backend is already the dead WebGPU
 * one. A fresh `WebGPURenderer({ forceWebGL: true })` picks `WebGLBackend` in
 * its constructor, which is the only place that choice is made.
 *
 * That memoisation is also what makes the swap safe to do inside the boot: a
 * later `renderer.init()` on the *replacement* — `scene-boot.ts` still calls
 * one — resolves from the cached promise instead of initialising twice.
 *
 * Nothing here imports three.js. `OpenableRenderer` is the four members this
 * needs, which `WebGPURenderer` satisfies structurally, so every branch below is
 * driven by a fake in `renderer-open.test.ts` on a machine with no adapter.
 */

import {
  factsOf,
  type RendererFacts,
  type RendererFallbackReason,
  type RendererFallbackRecord,
  type RendererPlan,
} from "./renderer-mode.ts";

/** What opening a renderer needs of one — `THREE.WebGPURenderer` satisfies this structurally. */
export interface OpenableRenderer {
  init(): Promise<unknown>;
  dispose(): void;
  /** three's own `Renderer.hasInitialized()`. See `discard` for why the failed attempt must not be disposed. */
  hasInitialized(): boolean;
  /** The canvas, so a discarded renderer's element can be taken back out of the container. */
  readonly domElement: { remove(): void };
}

export interface OpenedRenderer<R extends OpenableRenderer> {
  readonly renderer: R;
  /** What actually came up — `source: "fallback"` whenever the attempt above it failed. */
  readonly facts: RendererFacts;
}

/**
 * Why the attempt is passed in rather than built here.
 *
 * This function used to take one `build(mode)` and call it for the attempt as
 * well as the replacement. That reads correctly in isolation — and its unit test
 * asserting "a successful WebGPU boot builds one renderer" passed, because it
 * counted only the renderers *this function* asked for. The caller had already
 * built one: `flight-scene.ts` constructs `SceneState.renderer` eagerly, because
 * the field is not nullable, and `buildRenderer` appends its canvas to the
 * flight host. So an ordinary WebGPU boot created **two** renderers, requested
 * **two** GPU devices, and left **two** canvases in a host whose CSS gives each
 * one `width/height: 100%` of an `inset: 0` box.
 *
 * The visible result was the black screen of 2026-08-27: the abandoned canvas
 * kept three's unsized 300x150 drawing buffer and sat first in the host at
 * `y = 0`, covering the viewport, while the live 1280x800 one was pushed to
 * `y = 800` and out of the fold. Measured on the real GPU: mean luma **82.16**
 * at `17151d5` against **9.63** at `be65d35`, with a fully live HUD in both.
 *
 * Taking the attempt as an argument makes that composition unrepresentable —
 * there is no longer any way for this function to build a renderer in the plan's
 * mode — and the fallback builder is fixed at WebGL because that is the only
 * other thing it was ever allowed to build.
 */

/**
 * three's own adapter-failure message, quoted from
 * `WebGPUBackend.js`: `throw new Error( 'THREE.WebGPUBackend: Unable to create
 * WebGPU adapter.' )`. Matched on the distinctive middle rather than the whole
 * string so a punctuation change in three does not silently reclassify.
 */
const ADAPTER_FAILURE = /unable to create webgpu adapter/i;
/**
 * A `requestDevice` rejection. Browsers word these differently — Chromium's is
 * an `OperationError`, and the useful shared token is the method name or the
 * word "device" — so this is the looser of the two patterns and runs second.
 */
const DEVICE_FAILURE = /requestdevice|gpudevice|device/i;

export function classifyRendererInitFailure(error: unknown): RendererFallbackReason {
  const message = error instanceof Error ? error.message : String(error);
  if (ADAPTER_FAILURE.test(message)) return "adapter";
  if (DEVICE_FAILURE.test(message)) return "device";
  return "init";
}

export interface OpenRendererHooks {
  /**
   * Called exactly once, with the record to persist, when a WebGPU attempt
   * failed and the WebGL renderer took over. Not called for a plan that was
   * already WebGL — a remembered failure has nothing new to record, and a
   * pinned capture has no verdict to give.
   */
  readonly onFellBack?: (record: RendererFallbackRecord) => void;
  /** Called when the plan says so, before anything is built. See `RendererPlan.clearsStoredFallback`. */
  readonly onClearStoredFallback?: () => void;
}

/**
 * Builds a renderer for `plan`, initialises it, and — if that was a WebGPU
 * attempt and it failed — discards it and comes up on WebGL instead.
 *
 * Rejects only when the **WebGL** renderer also fails to initialise, which is
 * the genuinely unrenderable machine and the one case that still deserves the
 * error screen `FlightStatusOverlay` shows.
 */
export async function openRenderer<R extends OpenableRenderer>(
  plan: RendererPlan,
  /** The renderer the caller already built for `plan.mode`. See the note above for why it is not built here. */
  attempt: R,
  /** Builds the WebGL replacement. Called at most once, and only when `attempt.init()` rejected on a plan that may fall back. */
  buildFallback: () => R,
  hooks: OpenRendererHooks = {},
): Promise<OpenedRenderer<R>> {
  if (plan.clearsStoredFallback) hooks.onClearStoredFallback?.();
  try {
    await attempt.init();
    return { renderer: attempt, facts: factsOf(plan) };
  } catch (error) {
    if (!plan.mayFallBack) throw error;
    // The dead renderer's canvas is already in the container — `buildRenderer`
    // appends it — so it has to come out before the replacement's goes in, or
    // the page keeps a permanently blank element stacked over the picture.
    discard(attempt);
    const reason = classifyRendererInitFailure(error);
    hooks.onFellBack?.({ fallen: true, reason });
    const second = buildFallback();
    await second.init();
    return { renderer: second, facts: { mode: "webgl", source: "fallback", reason } };
  }
}

/**
 * Throws away a renderer whose `init()` rejected.
 *
 * **`dispose()` is deliberately NOT called on an uninitialised renderer, and
 * that is a three.js bug this port has to route around rather than an
 * optimisation.** `Renderer.dispose()` skips its entire teardown body when
 * `_initialized === false` — there is genuinely nothing to release, because the
 * backend never came up — and then ends with `this.setAnimationLoop( null )`.
 * That method is `async` and begins `if ( this._initialized === false ) await
 * this.init()`, so it **re-awaits the promise that has already rejected** and
 * discards the promise it makes doing so. The result is a real
 * `unhandledrejection` on every successful fallback boot: a red
 * "THREE.WebGPUBackend: Unable to create WebGPU adapter." in the console of a
 * page that recovered perfectly.
 *
 * Measured rather than reasoned: with `requestAdapter` forced to resolve `null`
 * in headed chromium, an `unhandledrejection` listener caught exactly one event,
 * 25 ms after the single adapter request, and it survived attaching a `.catch`
 * sink to the init promise — which is what ruled out the microtask race that
 * `scene-boot.ts` documents for its parallel fetches and pointed at a *second*
 * consumer of `_initPromise` instead. Guarding on three's own public
 * `hasInitialized()` removes it, and costs nothing: a renderer that never
 * initialised has no backend, no pipelines and no textures to free. The canvas
 * is the one real resource, and `remove()` above deals with it.
 *
 * The `remove()` is still guarded, for `flight-scene.ts`'s reason: this object
 * is already lost, and a throw while tidying it up would abort the fallback and
 * turn a recoverable machine into the error screen.
 */
function discard(renderer: OpenableRenderer): void {
  try {
    renderer.domElement.remove();
  } catch {
    // Nothing to do: the canvas was never parented, which is not a failure.
  }
  if (!renderer.hasInitialized()) return;
  try {
    renderer.dispose();
  } catch {
    // Likewise — the attempt is already lost; the replacement is what matters.
  }
}

/** The one member of `Location` the reload path uses, so a test can supply a recorder. */
export interface ReloadTarget {
  reload(): void;
}

export interface DeviceLossHandling {
  /** The record to persist before reloading, so the next boot comes up on WebGL without probing. */
  readonly record: RendererFallbackRecord;
  /** What the player is told, in the seconds before the page goes. */
  readonly message: string;
}

/**
 * How long the loss message stays up before the reload. Long enough to read one
 * short sentence, short enough that nobody thinks the game has hung — and it is
 * a *named* constant because the test that asserts the reload happens has to
 * wait exactly this long and must not guess.
 */
export const DEVICE_LOSS_RELOAD_DELAY_MS = 2500;

/**
 * What a mid-sortie `GPUDevice` loss turns into.
 *
 * **A live renderer rebuild is out of scope and would not be a small feature.**
 * Every material, texture, geometry and compiled pipeline in the scene is bound
 * to the dead device, and three's `WebGPURenderer` has no supported path for
 * re-hosting them; the honest recovery is the one the browser itself would
 * force, which is to load the page again.
 *
 * **What the reload keeps, and what it does not.** The URL is untouched, so the
 * route and its mission selection survive, and the service worker
 * (`docs/engineering/offline-caching-design.md`) serves the airframe, the
 * region and the sound bank from cache, which is the expensive part of a cold
 * boot. **The sortie itself does not survive** — the sim's state lives in
 * memory and there is no autosave to come back to — so the message says so
 * rather than letting a pilot discover it at the runway.
 */
export function deviceLossHandling(reason: string, message: string): DeviceLossHandling {
  const detail = reason.trim().length > 0 ? `${reason}: ${message}` : message;
  return {
    record: { fallen: true, reason: "device-lost" },
    message: `The graphics device was lost (${detail}). Reloading on WebGL — this flight is lost, but the mission can be flown again.`,
  };
}
